import React, { useEffect, useRef, useState } from 'react';
import {
  Search, Upload, Plus, Play, Trash2, X, Loader2, Eye, Video as VideoIcon,
  ShieldCheck, AlertTriangle, Tag,
} from 'lucide-react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import {
  fetchSeminarVideos, uploadSeminarVideo, deleteSeminarVideo, seminarVideoStreamUrl,
} from '../services/api';
import { humanizeApiError } from '../lib/errors';

const CATEGORIES = ['', 'nutrition', 'exercise', 'sleep', 'mental_wellness', 'longevity', 'supplements', 'lifestyle', 'general'];
const CAT_LABELS = { nutrition: 'Nutrition', exercise: 'Exercise', sleep: 'Sleep', mental_wellness: 'Mental Wellness', longevity: 'Longevity', supplements: 'Supplements', lifestyle: 'Lifestyle', general: 'General' };
const CAT_COLORS = { nutrition: '#66BB6A', exercise: '#EF5350', sleep: '#5C6BC0', mental_wellness: '#FF9800', longevity: '#6B8E23', supplements: '#8D6E63', lifestyle: '#42A5F5', general: '#78909C' };
const TIER_LABELS = ['Free', 'Explorer', 'Analyst', 'Optimizer', 'Elite'];

export default function VideoLibraryTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [active, setActive] = useState(null); // currently-playing video doc
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await fetchSeminarVideos({ q: q || undefined, category: cat || undefined, limit: 100 });
      setVideos(r.videos || []);
    } catch (e) {
      setError(humanizeApiError(e, 'Failed to load videos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cat]);

  const onSearchSubmit = (e) => { e.preventDefault(); reload(); };

  const onDelete = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? This cannot be undone.`)) return;
    try {
      await deleteSeminarVideo(v.id);
      await reload();
    } catch (e) {
      setError(humanizeApiError(e, 'Delete failed'));
    }
  };

  return (
    <div data-testid="video-library-tab">
      {/* Search + Filter row */}
      <form onSubmit={onSearchSubmit} style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 240 }}>
          <Search size={18} color={COLORS.textSecondary} style={{ position: 'absolute', left: 14, top: 12 }} />
          <input
            data-testid="library-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by keyword, title, topic..."
            style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 14, backgroundColor: COLORS.surface, color: COLORS.textPrimary }}
          />
        </div>
        <button type="submit" data-testid="library-search-btn" style={{ padding: '10px 20px', borderRadius: 12, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Search
        </button>
        {isAdmin && (
          <button type="button" data-testid="library-upload-btn" onClick={() => setShowUpload(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 12, border: 'none', backgroundColor: '#0F766E', color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Plus size={14} /> Upload Video
          </button>
        )}
      </form>

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {CATEGORIES.map(c => (
          <button key={c || 'all'} data-testid={`library-cat-${c || 'all'}`} onClick={() => setCat(c)}
            style={{ padding: '6px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', backgroundColor: cat === c ? COLORS.primary : COLORS.surface, color: cat === c ? '#FFF' : COLORS.textSecondary }}>
            {c ? (CAT_LABELS[c] || c) : 'All'}
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="library-error" style={{ backgroundColor: '#FEE2E2', color: '#B22222', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}><Loader2 size={20} className="spin" /></div>
      ) : videos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <VideoIcon size={56} color={COLORS.border} />
          <h3 style={{ color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>
            {q || cat ? 'No videos match your search.' : 'Library is empty'}
          </h3>
          <p style={{ color: COLORS.textSecondary, marginTop: 8 }}>
            {isAdmin ? 'Upload a seminar video to get started.' : 'Check back soon — admins curate new videos every week.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {videos.map(v => {
            const cc = CAT_COLORS[v.category] || COLORS.primary;
            return (
              <div key={v.id} data-testid={`library-card-${v.id}`}
                style={{ backgroundColor: COLORS.surface, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
                <div onClick={() => setActive(v)}
                  style={{ position: 'relative', backgroundColor: '#0f172a', cursor: 'pointer',
                           backgroundImage: v.thumbnail_url ? `url(${v.thumbnail_url})` : 'linear-gradient(135deg, #1f2937, #0f172a)',
                           backgroundSize: 'cover', backgroundPosition: 'center', aspectRatio: '16/9' }}>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.25)' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Play size={26} color={COLORS.primary} />
                    </div>
                  </div>
                  {v.min_tier > 0 && (
                    <div style={{ position: 'absolute', top: 8, left: 8, padding: '3px 8px', borderRadius: 6, backgroundColor: 'rgba(15,118,110,0.95)', color: '#FFF', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ShieldCheck size={11} /> {TIER_LABELS[v.min_tier] || `Tier ${v.min_tier}`}+
                    </div>
                  )}
                  {v.duration_sec > 0 && (
                    <div style={{ position: 'absolute', bottom: 8, right: 8, padding: '3px 7px', borderRadius: 4, backgroundColor: 'rgba(0,0,0,0.7)', color: '#FFF', fontSize: 11, fontWeight: 600 }}>
                      {fmtDur(v.duration_sec)}
                    </div>
                  )}
                </div>
                <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, backgroundColor: `${cc}18`, color: cc, textTransform: 'capitalize' }}>{CAT_LABELS[v.category] || v.category}</span>
                    {v.view_count > 0 && (
                      <span style={{ fontSize: 11, color: COLORS.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <Eye size={11} /> {v.view_count}
                      </span>
                    )}
                  </div>
                  <h3 onClick={() => setActive(v)} style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0, marginBottom: 4, cursor: 'pointer', lineHeight: 1.3 }}>{v.title}</h3>
                  {v.description && (
                    <p style={{ fontSize: 12.5, color: COLORS.textSecondary, lineHeight: 1.45, margin: 0, marginBottom: 8 }}>
                      {v.description.slice(0, 110)}{v.description.length > 110 ? '…' : ''}
                    </p>
                  )}
                  {v.keywords?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 'auto', paddingTop: 6 }}>
                      {v.keywords.slice(0, 4).map(k => (
                        <span key={k} style={{ fontSize: 10, color: COLORS.textSecondary, padding: '2px 6px', borderRadius: 4, backgroundColor: `${COLORS.primary}10`, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Tag size={9} /> {k}
                        </span>
                      ))}
                    </div>
                  )}
                  {isAdmin && (
                    <button data-testid={`library-delete-${v.id}`} onClick={(e) => { e.stopPropagation(); onDelete(v); }}
                      style={{ marginTop: 8, alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#B22222' }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); reload(); }} />}
      {active && <PlayerModal video={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function fmtDur(s) {
  if (!s || s < 0) return '';
  const m = Math.floor(s / 60); const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// ── Upload modal ─────────────────────────────────────────────────────────

function UploadModal({ onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [minTier, setMinTier] = useState(0);
  const [thumbUrl, setThumbUrl] = useState('');
  const [mode, setMode] = useState('file'); // file | url
  const [extUrl, setExtUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!title.trim()) return setErr('Title is required');
    const file = mode === 'file' ? fileRef.current?.files?.[0] : null;
    if (mode === 'file' && !file) return setErr('Pick a video file');
    if (mode === 'url' && !extUrl.trim()) return setErr('Enter a video URL');
    setSubmitting(true);
    try {
      await uploadSeminarVideo({
        title: title.trim(), description, category, manual_tags: tags,
        min_tier: minTier, thumbnail_url: thumbUrl, duration_sec: duration,
        url: mode === 'url' ? extUrl.trim() : '', file: mode === 'file' ? file : null,
      });
      onDone();
    } catch (e) {
      setErr(humanizeApiError(e, 'Upload failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <form data-testid="library-upload-form" onSubmit={submit} onClick={e => e.stopPropagation()}
        style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 26, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: COLORS.textPrimary, fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Upload size={20} color={COLORS.primary} /> Upload Seminar Video
          </h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${COLORS.border}`, marginBottom: 12 }}>
          <button type="button" data-testid="library-upload-mode-file" onClick={() => setMode('file')}
            style={{ flex: 1, padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, backgroundColor: mode === 'file' ? COLORS.primary : '#FFF', color: mode === 'file' ? '#FFF' : COLORS.textSecondary }}>
            Upload File (≤200 MB)
          </button>
          <button type="button" data-testid="library-upload-mode-url" onClick={() => setMode('url')}
            style={{ flex: 1, padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, backgroundColor: mode === 'url' ? COLORS.primary : '#FFF', color: mode === 'url' ? '#FFF' : COLORS.textSecondary }}>
            Paste URL (YouTube/mp4)
          </button>
        </div>

        {mode === 'file' ? (
          <input ref={fileRef} type="file" accept="video/*" data-testid="library-upload-file-input"
            style={{ width: '100%', padding: 8, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 12 }} />
        ) : (
          <input type="url" data-testid="library-upload-url-input" value={extUrl} onChange={e => setExtUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
            style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 12, fontSize: 14 }} />
        )}

        <input data-testid="library-upload-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title *"
          style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 10, fontSize: 14 }} />
        <textarea data-testid="library-upload-description" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Description (helps GPT-4o auto-suggest keywords)" rows={3}
          style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <select data-testid="library-upload-category" value={category} onChange={e => setCategory(e.target.value)}
            style={{ padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, backgroundColor: '#FFF' }}>
            {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
          </select>
          <select data-testid="library-upload-tier" value={minTier} onChange={e => setMinTier(parseInt(e.target.value, 10))}
            style={{ padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, backgroundColor: '#FFF' }}>
            {TIER_LABELS.map((label, i) => <option key={i} value={i}>{label}{i === 0 ? ' (everyone)' : `+ (Tier ${i}+)`}</option>)}
          </select>
        </div>
        <input data-testid="library-upload-tags" value={tags} onChange={e => setTags(e.target.value)}
          placeholder="Manual tags (comma-separated, optional)"
          style={{ width: '100%', padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 10, fontSize: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <input value={thumbUrl} onChange={e => setThumbUrl(e.target.value)} placeholder="Thumbnail URL (optional)"
            style={{ padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14 }} />
          <input type="number" min={0} value={Number.isFinite(duration) ? duration : 0}
            onChange={e => { const n = parseInt(e.target.value, 10); setDuration(Number.isFinite(n) ? n : 0); }}
            placeholder="Duration (sec)"
            style={{ padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14 }} />
        </div>
        <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '0 0 14px', lineHeight: 1.5 }}>
          GPT-4o will auto-suggest 5–10 keywords from your title + description. They merge with your manual tags into the search index.
        </p>

        {err && (
          <div data-testid="library-upload-error" style={{ backgroundColor: '#FEE2E2', color: '#B22222', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
            {err}
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="library-upload-submit"
          style={{ width: '100%', padding: 12, borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: submitting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: submitting ? 0.7 : 1 }}>
          {submitting ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
          {submitting ? 'Uploading & extracting keywords…' : 'Save'}
        </button>
      </form>
    </div>
  );
}

// ── Player modal ────────────────────────────────────────────────────────

function PlayerModal({ video, onClose }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let revoke = null;
    if (video.source_type === 'upload') {
      // Authenticated fetch → blob URL (browsers can't add Authorization to <video src>)
      const token = localStorage.getItem('auth_token');
      setLoading(true);
      fetch(seminarVideoStreamUrl(video.id), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(async r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          revoke = url;
          setBlobUrl(url);
        })
        .catch(e => setErr(`Could not load video: ${e.message}`))
        .finally(() => setLoading(false));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [video]);

  const isYouTube = (u) => /youtu\.?be/.test(u || '');
  const ytEmbed = (u) => {
    try {
      const url = new URL(u);
      if (url.hostname.includes('youtu.be')) return `https://www.youtube.com/embed${url.pathname}`;
      const v = url.searchParams.get('v');
      return v ? `https://www.youtube.com/embed/${v}` : u;
    } catch { return u; }
  };

  return (
    <div data-testid="library-player-modal" onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 920, backgroundColor: '#000', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, background: 'rgba(0,0,0,0.5)', border: 'none', cursor: 'pointer', borderRadius: 9999, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <X size={20} color="#FFF" />
        </button>
        <div style={{ aspectRatio: '16/9', width: '100%', backgroundColor: '#000' }}>
          {video.source_type === 'url' ? (
            isYouTube(video.url) ? (
              <iframe data-testid="library-player-iframe" src={ytEmbed(video.url)} title={video.title} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen />
            ) : (
              <video data-testid="library-player-video-url" src={video.url} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            )
          ) : loading ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF' }}>
              <Loader2 size={28} className="spin" />
            </div>
          ) : err ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FCA5A5', padding: 20, textAlign: 'center' }}>{err}</div>
          ) : (
            <video data-testid="library-player-video" src={blobUrl} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
        </div>
        <div style={{ padding: 16, color: '#FFF' }}>
          <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 18 }}>{video.title}</h3>
          {video.description && <p style={{ marginTop: 6, fontSize: 13, color: '#CBD5E1', lineHeight: 1.5 }}>{video.description}</p>}
          {video.keywords?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
              {video.keywords.map(k => (
                <span key={k} style={{ fontSize: 11, color: '#FFF', padding: '2px 8px', borderRadius: 9999, backgroundColor: 'rgba(255,255,255,0.1)' }}>{k}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
